// ReportRowActions — the Reports table's Actions cell: a kebab Dropdown of the
// two per-report actions. View submissions opens the same screen a row click
// does; Bookmark / Remove bookmark is PATCH /forms/bookmark either way — the
// page picks the body's `bookmark` value. Owns its own open state (one row's
// menu at a time); the flows live in ReportsPage via the callbacks. Mirrors
// FormRowActions.

import { useState, type MouseEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Dropdown, Icon, IconButton } from '@/components/common';

interface ReportRowActionsProps {
  /** View submissions — the same navigation a row click takes. */
  onView: () => void;
  /** Bookmark, or un-bookmark when `bookmarked`. */
  onBookmark: () => void;
  /** This row is known to be bookmarked (the Bookmarks tab, where membership in
   *  the filtered list IS the state — the rows carry no bookmark flag of their
   *  own). Swaps the menu item's label + glyph, nothing else. */
  bookmarked?: boolean;
}

const ReportRowActions = ({ onView, onBookmark, bookmarked = false }: ReportRowActionsProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  // The table row navigates to /submissions on click — keep the menu's clicks
  // from bubbling into it.
  const stop = (e: MouseEvent) => e.stopPropagation();

  const item = (label: string, icon: string, onClick: () => void): ReactNode => (
    <button
      type='button'
      className='app-dropdown__item'
      role='menuitem'
      onClick={() => {
        setOpen(false);
        onClick();
      }}
    >
      <Icon name={icon} size={15} />
      <span className='app-dropdown__item-label'>{label}</span>
    </button>
  );

  return (
    <span className='reports-table__actions' onClick={stop}>
      <Dropdown
        open={open}
        onClose={() => setOpen(false)}
        align='end'
        width={220}
        portal
        trigger={
          <IconButton
            icon='ellipsis-vertical'
            variant='ghost'
            size='sm'
            aria-label={t('reports_col_actions')}
            aria-expanded={open}
            onClick={() => setOpen((o) => !o)}
          />
        }
      >
        {item(t('reports_action_view_submissions'), 'table-2', onView)}
        {bookmarked
          ? item(t('forms_action_unbookmark'), 'bookmark-x', onBookmark)
          : item(t('forms_action_bookmark'), 'bookmark', onBookmark)}
      </Dropdown>
    </span>
  );
};

export default ReportRowActions;
